import { useState } from 'react';
import ResourceHeaderCard from './ResourceHeaderCard';
import {
  useResourcesInIncident,
  ResourceInIncident,
  useDeleteResourceInIncident,
} from 'components/IAP/hooks/resourceInIncidentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import CheckOutDrawer from 'components/IAP/Resources/Drawers/CheckOutDrawer';
import { RESOURCE_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import { DemobilizedModal } from './DemobilizedModal';
import dayjs from 'dayjs';

const columns: {
  key: string;
  label: string;
}[] = [
  {
    key: '221',
    label: '221',
  },
  {
    key: 'resource_set_kind',
    label: 'Kind',
  },
  {
    key: 'resource_set_type',
    label: 'Type',
  },
  {
    key: 'resource_name',
    label: 'Resource ID',
  },
  {
    key: 'checkin_datetime',
    label: 'Date/Time Check-In',
  },
  {
    key: 'personnel_count',
    label: '# of Personnel',
  },
  {
    key: 'null',
    label: 'Actions',
  },
];

export const CheckedOutResources: React.FC<{
  handleResourceUpdate: (updatedResource: Partial<ResourceInIncident>) => void;
  searchTerm: string;
}> = ({ handleResourceUpdate, searchTerm }) => {
  const [showCheckOutDrawer, setShowCheckOutDrawer] = useState(false);
  const [showDemobilizedModal, setShowDemobilizedModal] = useState(false);
  const [selectedResourceInIncident, setSelectedResourceInIncident] =
    useState<ResourceInIncident | null>(null);
  const { data: resourceInIncident } = useResourcesInIncident();
  const deleteResource = useDeleteResourceInIncident();

  return (
    <>
      <ResourceHeaderCard
        filters={{}}
        onFilterChange={() => {}}
        columns={columns}
        grid="checked-out-card-grid"
      />
      {resourceInIncident
        ?.filter(
          (res) =>
            res.current_status === RESOURCE_STATUSES.CHECKED_OUT &&
            (!searchTerm ||
              (res.resource_name ?? '')
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
        .map((resource) => (
          <CheckedOutResourceRow
            key={resource.id}
            resource={resource}
            setSelectedResourceInIncident={setSelectedResourceInIncident}
            setShowCheckOutDrawer={setShowCheckOutDrawer}
            deleteResource={deleteResource}
            setShowDemobilizedModal={setShowDemobilizedModal}
          />
        ))}
      {showCheckOutDrawer && selectedResourceInIncident && (
        <CheckOutDrawer
          resource={selectedResourceInIncident}
          onClose={() => {
            setShowCheckOutDrawer(false);
          }}
          onSave={(data) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
              status_since: new Date().toISOString(),
            });
            setShowCheckOutDrawer(false);
          }}
          isUpdate
        />
      )}
      {showDemobilizedModal && selectedResourceInIncident && (
        <DemobilizedModal
          resource={selectedResourceInIncident}
          setShowDemobilizedModal={setShowDemobilizedModal}
          onSave={(data: any) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
              status_since: new Date().toISOString(),
            });
          }}
        />
      )}
    </>
  );
};

const CheckedOutResourceRow: React.FC<{
  resource: ResourceInIncident;
  setSelectedResourceInIncident: (resource: ResourceInIncident) => void;
  setShowCheckOutDrawer: (show: boolean) => void;
  deleteResource: any;
  setShowDemobilizedModal: (show: boolean) => void;
}> = ({
  resource,
  setSelectedResourceInIncident,
  setShowCheckOutDrawer,
  deleteResource,
  setShowDemobilizedModal,
}) => {
  return (
    <div className="checked-out-card-grid">
      <div></div>
      <div>
        <StylishNewButton
          className="button--secondary"
          onClick={() => {
            setSelectedResourceInIncident(resource);
            setShowDemobilizedModal(true);
          }}
        >
          221
        </StylishNewButton>
      </div>
      <div>{resource.resource_set_kind}</div>
      <div>{resource.resource_set_type}</div>
      <div>{resource.resource_name}</div>
      <div>
        {resource.checkin_datetime &&
          dayjs(resource.checkin_datetime).format('YYYY-MM-DD HH:mm')}
      </div>
      <div>{resource.personnel_count}</div>
      <div className="d-flex flex-nowrap gap-1">
        <StylishNewButton
          className="button--icon"
          onClick={() => {
            setSelectedResourceInIncident(resource);
            setShowCheckOutDrawer(true);
          }}
        >
          <SharedIcon iconName="edit" size="sm" />
        </StylishNewButton>
        <StylishNewButton
          className="button--icon"
          onClick={() => deleteResource.mutate(resource.id)}
        >
          <SharedIcon iconName="delete" size="sm" />
        </StylishNewButton>
      </div>
    </div>
  );
};
